import React, { useState, useRef, useEffect, useCallback } from 'react'
import { Sticker } from '@renderer/types/configState'

interface StickerPlacement {
  id: string
  stickerId: string
  x: number
  y: number
  size: number
  timestamp: number
}

type BrushType = 'solid' | 'dashed' | 'dotted' | 'marker' | 'pencil' | 'airbrush'

interface BrushStroke {
  id: string
  points: Array<{ x: number; y: number }>
  color: string
  size: number
  brushType: BrushType
  timestamp: number
}

interface StickerEditorProps {
  stripUrl: string
  stickerPlacements: StickerPlacement[]
  onStickerPlacement: (x: number, y: number) => void
  stickers?: Sticker[]
  className?: string
  brushStrokes?: BrushStroke[]
  onBrushStroke?: (stroke: BrushStroke) => void
  brushColor?: string
  brushSize?: number
  brushType?: BrushType
  mode?: 'sticker' | 'brush'
  selectedItemId?: string | null
  onItemSelect?: (itemId: string | null, itemType: 'sticker' | 'brush' | null) => void
  onStickerUpdate?: (stickerId: string, x: number, y: number) => void
  onBrushStrokeUpdate?: (strokeId: string, points: Array<{ x: number; y: number }>) => void
}

const StickerEditor: React.FC<StickerEditorProps> = ({
  stripUrl,
  stickerPlacements,
  onStickerPlacement,
  stickers = [],
  className = '',
  brushStrokes = [],
  onBrushStroke,
  brushColor = '#000000',
  brushSize = 10,
  brushType = 'solid',
  mode = 'sticker',
  selectedItemId = null,
  onItemSelect,
  onStickerUpdate,
  onBrushStrokeUpdate
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const [isImageLoaded, setIsImageLoaded] = useState(false)
  const [imageDimensions, setImageDimensions] = useState({ width: 0, height: 0 })
  const [canvasDimensions, setCanvasDimensions] = useState({ width: 0, height: 0 })
  const stickerImagesCache = useRef<Map<string, HTMLImageElement>>(new Map())
  const stickerLoadingPromises = useRef<Map<string, Promise<HTMLImageElement>>>(new Map())
  const stripImageRef = useRef<HTMLImageElement | null>(null)
  const renderTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const renderFnRef = useRef<(() => void) | null>(null)
  const livePreviewTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  // Brush drawing state
  const isDrawingRef = useRef(false)
  const currentStrokeRef = useRef<BrushStroke | null>(null)
  const lastPointRef = useRef<{ x: number; y: number } | null>(null)

  // Drag state
  const isDraggingRef = useRef(false)
  const dragStartPosRef = useRef<{ x: number; y: number } | null>(null)
  const dragOffsetRef = useRef<{ x: number; y: number } | null>(null)
  const dragItemIdRef = useRef<string | null>(null)
  const dragItemTypeRef = useRef<'sticker' | 'brush' | null>(null)

  // Pre-load all sticker images when stickers change
  useEffect(() => {
    if (stickers.length === 0) return

    const loadStickerImage = (sticker: { _id: string; url?: string }): Promise<HTMLImageElement> => {
      const cached = stickerImagesCache.current.get(sticker._id)
      if (cached && cached.complete && cached.naturalWidth > 0) {
        return Promise.resolve(cached)
      }

      const existingPromise = stickerLoadingPromises.current.get(sticker._id)
      if (existingPromise) {
        return existingPromise
      }

      if (!sticker.url) {
        return Promise.reject(new Error(`Sticker ${sticker._id} has no URL`))
      }

      const promise = new Promise<HTMLImageElement>((resolve, reject): void => {
        const img = new Image()
        img.crossOrigin = 'anonymous'
        
        img.onload = (): void => {
          stickerImagesCache.current.set(sticker._id, img)
          stickerLoadingPromises.current.delete(sticker._id)
          resolve(img)
        }
        
        img.onerror = (error: Event | string): void => {
          stickerLoadingPromises.current.delete(sticker._id)
          console.error(`Failed to load sticker image: ${sticker.url}`, { stickerId: sticker._id })
          reject(error)
        }
        
        img.src = sticker.url!
      })

      stickerLoadingPromises.current.set(sticker._id, promise)
      return promise
    }

    // Pre-load all sticker images in parallel
    const loadPromises = stickers
      .filter((s) => s.url)
      .map((sticker) => loadStickerImage(sticker).catch((error) => {
        console.warn(`Failed to pre-load sticker ${sticker._id}:`, error)
        return null
      }))

    Promise.all(loadPromises).then(() => {
      // Trigger re-render when all images are loaded
      if (renderFnRef.current) {
        renderFnRef.current()
      }
    })

    // Cleanup: remove loading promises for stickers that are no longer in the list
    const currentStickerIds = new Set(stickers.map((s) => s._id))
    stickerLoadingPromises.current.forEach((_, stickerId) => {
      if (!currentStickerIds.has(stickerId)) {
        stickerLoadingPromises.current.delete(stickerId)
      }
    })
  }, [stickers])

  // Load the strip image once and cache it
  useEffect(() => {
    if (!stripUrl) {
      setImageDimensions((prev) => {
        if (prev.width === 0 && prev.height === 0) return prev
        return { width: 0, height: 0 }
      })
      setIsImageLoaded((prev) => {
        if (!prev) return prev
        return false
      })
      stripImageRef.current = null
      return
    }

    // Only reset if stripUrl actually changed (check against current ref)
    const currentUrl = stripImageRef.current?.src || ''
    if (currentUrl === stripUrl) {
      // Same URL, don't reload
      return
    }

    // Clear any existing timeout
    if (renderTimeoutRef.current) {
      clearTimeout(renderTimeoutRef.current)
      renderTimeoutRef.current = null
    }

    // Reset state only if URL changed
    setIsImageLoaded((prev) => {
      if (!prev) return prev
      return false
    })
    stripImageRef.current = null

    const img = new Image()
    img.crossOrigin = 'anonymous'

    let isMounted = true

    img.onload = (): void => {
      if (!isMounted) return

      const newDimensions = { width: img.naturalWidth, height: img.naturalHeight }
      setImageDimensions((prev) => {
        // Only update if dimensions actually changed
        if (prev.width === newDimensions.width && prev.height === newDimensions.height) {
          return prev
        }
        return newDimensions
      })
      setIsImageLoaded((prev) => {
        if (prev) return prev
        return true
      })
      stripImageRef.current = img

      // Trigger initial render after image loads (debounced)
      if (renderTimeoutRef.current) {
        clearTimeout(renderTimeoutRef.current)
      }
      renderTimeoutRef.current = setTimeout(() => {
        // Call render via ref to avoid circular dependency
        if (isMounted && renderFnRef.current) {
          renderFnRef.current()
        }
      }, 0)
    }

    img.onerror = (): void => {
      if (isMounted) {
        console.error('Failed to load strip image:', stripUrl)
        setIsImageLoaded((prev) => {
          if (!prev) return prev
          return false
        })
      }
    }

    img.src = stripUrl

    return (): void => {
      isMounted = false
      if (renderTimeoutRef.current) {
        clearTimeout(renderTimeoutRef.current)
        renderTimeoutRef.current = null
      }
    }
  }, [stripUrl])

  // Update canvas dimensions when container size changes
  useEffect(() => {
    const updateCanvasSize = (): void => {
      const container = containerRef.current
      if (!container) return

      const rect = container.getBoundingClientRect()
      const newWidth = rect.width
      const newHeight = rect.height

      // Only update if dimensions actually changed to prevent infinite loops
      setCanvasDimensions((prev) => {
        if (prev.width === newWidth && prev.height === newHeight) {
          return prev
        }
        return { width: newWidth, height: newHeight }
      })
    }

    updateCanvasSize()
    window.addEventListener('resize', updateCanvasSize)
    return (): void => window.removeEventListener('resize', updateCanvasSize)
  }, [])

  // Helper function to get draw area dimensions (consistent with getClickPosition)
  const getDrawArea = useCallback((): { drawWidth: number; drawHeight: number; drawX: number; drawY: number; scaleX: number; scaleY: number } | null => {
    if (!isImageLoaded) return null

    const displayWidth = canvasDimensions.width
    const displayHeight = canvasDimensions.height
    const imageAspect = imageDimensions.width / imageDimensions.height
    const canvasAspect = displayWidth / displayHeight

    let drawWidth: number, drawHeight: number, drawX: number, drawY: number

    if (imageAspect > canvasAspect) {
      drawWidth = displayWidth
      drawHeight = displayWidth / imageAspect
      drawX = 0
      drawY = (displayHeight - drawHeight) / 2
    } else {
      drawHeight = displayHeight
      drawWidth = displayHeight * imageAspect
      drawX = (displayWidth - drawWidth) / 2
      drawY = 0
    }

    const scaleX = drawWidth / imageDimensions.width
    const scaleY = drawHeight / imageDimensions.height

    return { drawWidth, drawHeight, drawX, drawY, scaleX, scaleY }
  }, [isImageLoaded, canvasDimensions, imageDimensions])

  // Hit detection: check if a point is within a sticker
  const hitTestSticker = useCallback((point: { x: number; y: number }, placement: StickerPlacement, stickerImg: HTMLImageElement): boolean => {
    if (!stickerImg.complete || stickerImg.naturalWidth === 0) return false

    const stickerAspect = stickerImg.naturalWidth / stickerImg.naturalHeight
    let stickerWidth: number, stickerHeight: number

    if (stickerAspect >= 1) {
      stickerWidth = placement.size
      stickerHeight = stickerWidth / stickerAspect
    } else {
      stickerHeight = placement.size
      stickerWidth = stickerHeight * stickerAspect
    }

    const halfWidth = stickerWidth / 2
    const halfHeight = stickerHeight / 2

    return (
      point.x >= placement.x - halfWidth &&
      point.x <= placement.x + halfWidth &&
      point.y >= placement.y - halfHeight &&
      point.y <= placement.y + halfHeight
    )
  }, [])

  // Hit detection: check if a point is near a brush stroke
  const hitTestBrushStroke = useCallback((point: { x: number; y: number }, stroke: BrushStroke, threshold: number = 20): boolean => {
    if (stroke.points.length === 0) return false

    for (let i = 0; i < stroke.points.length - 1; i++) {
      const p1 = stroke.points[i]
      const p2 = stroke.points[i + 1]

      // Calculate distance from point to line segment
      const dx = p2.x - p1.x
      const dy = p2.y - p1.y
      const lengthSquared = dx * dx + dy * dy

      if (lengthSquared === 0) {
        // Single point
        const dist = Math.sqrt((point.x - p1.x) ** 2 + (point.y - p1.y) ** 2)
        if (dist <= threshold + stroke.size / 2) return true
        continue
      }

      const t = Math.max(0, Math.min(1, ((point.x - p1.x) * dx + (point.y - p1.y) * dy) / lengthSquared))
      const projX = p1.x + t * dx
      const projY = p1.y + t * dy

      const dist = Math.sqrt((point.x - projX) ** 2 + (point.y - projY) ** 2)
      if (dist <= threshold + stroke.size / 2) return true
    }

    return false
  }, [])

  // Hit test: find which item (if any) was clicked
  const hitTest = useCallback((point: { x: number; y: number }): { itemId: string; itemType: 'sticker' | 'brush' } | null => {
    // Check stickers first (topmost), in reverse order (most recent first)
    for (let i = stickerPlacements.length - 1; i >= 0; i--) {
      const placement = stickerPlacements[i]
      const sticker = stickers.find((s) => s._id === placement.stickerId)
      if (!sticker) continue

      let stickerImg = stickerImagesCache.current.get(placement.stickerId)
      if (!stickerImg) {
        stickerImg = new Image()
        stickerImg.crossOrigin = 'anonymous'
        stickerImg.src = sticker.url
        stickerImagesCache.current.set(placement.stickerId, stickerImg)
      }

      if (stickerImg.complete && hitTestSticker(point, placement, stickerImg)) {
        return { itemId: placement.id, itemType: 'sticker' }
      }
    }

    // Check brush strokes, in reverse order (most recent first)
    for (let i = brushStrokes.length - 1; i >= 0; i--) {
      const stroke = brushStrokes[i]
      if (hitTestBrushStroke(point, stroke)) {
        return { itemId: stroke.id, itemType: 'brush' }
      }
    }

    return null
  }, [stickerPlacements, stickers, brushStrokes, hitTestSticker, hitTestBrushStroke])

  // Get click position relative to canvas
  const getClickPosition = (
    e: React.MouseEvent | React.TouchEvent
  ): { x: number; y: number } | null => {
    const canvas = canvasRef.current
    if (!canvas || !isImageLoaded) return null

    const rect = canvas.getBoundingClientRect()
    // Use display dimensions (not actual canvas dimensions which include DPR scaling)
    const displayWidth = rect.width
    const displayHeight = rect.height

    // Calculate how the image is drawn on the display
    const imageAspect = imageDimensions.width / imageDimensions.height
    const canvasAspect = displayWidth / displayHeight

    let drawWidth: number, drawHeight: number, drawX: number, drawY: number

    if (imageAspect > canvasAspect) {
      // Image is wider than canvas
      drawWidth = displayWidth
      drawHeight = displayWidth / imageAspect
      drawX = 0
      drawY = (displayHeight - drawHeight) / 2
    } else {
      // Image is taller than canvas
      drawHeight = displayHeight
      drawWidth = displayHeight * imageAspect
      drawX = (displayWidth - drawWidth) / 2
      drawY = 0
    }

    // Scale from display coordinates to image coordinates
    const scaleX = imageDimensions.width / drawWidth
    const scaleY = imageDimensions.height / drawHeight

    let clientX: number, clientY: number

    if ('touches' in e) {
      if (e.touches.length === 0) return null
      clientX = e.touches[0].clientX
      clientY = e.touches[0].clientY
    } else {
      clientX = e.clientX
      clientY = e.clientY
    }

    // Get click position relative to canvas display
    const clickX = clientX - rect.left
    const clickY = clientY - rect.top

    // Convert to coordinates relative to the drawn image area
    const relativeX = clickX - drawX
    const relativeY = clickY - drawY

    // Convert to image pixel coordinates
    const canvasX = relativeX * scaleX
    const canvasY = relativeY * scaleY

    return { x: canvasX, y: canvasY }
  }

  // Handle pointer down for sticker placement or brush start
  const handlePointerDown = (e: React.MouseEvent | React.TouchEvent): void => {
    const position = getClickPosition(e)
    if (!position) return

    // Check if clicking on existing item (for selection/drag)
    const hit = hitTest(position)
    
    if (hit) {
      // Select the item
      if (onItemSelect) {
        onItemSelect(hit.itemId, hit.itemType)
      }

      // Enter drag mode if in sticker mode and hit a sticker
      if (mode === 'sticker' && hit.itemType === 'sticker' && onStickerUpdate) {
        const placement = stickerPlacements.find((p) => p.id === hit.itemId)
        if (placement) {
          isDraggingRef.current = true
          dragStartPosRef.current = position
          dragOffsetRef.current = {
            x: position.x - placement.x,
            y: position.y - placement.y
          }
          dragItemIdRef.current = hit.itemId
          dragItemTypeRef.current = 'sticker'
          e.preventDefault()
          return
        }
      }

      // Enter drag mode if in brush mode and hit a brush stroke
      if (mode === 'brush' && hit.itemType === 'brush' && onBrushStrokeUpdate) {
        const stroke = brushStrokes.find((s) => s.id === hit.itemId)
        if (stroke) {
          isDraggingRef.current = true
          dragStartPosRef.current = position
          // Calculate offset from first point
          dragOffsetRef.current = {
            x: position.x - stroke.points[0].x,
            y: position.y - stroke.points[0].y
          }
          dragItemIdRef.current = hit.itemId
          dragItemTypeRef.current = 'brush'
          e.preventDefault()
          return
        }
      }
    } else {
      // Deselect if clicking on empty area
      if (onItemSelect) {
        onItemSelect(null, null)
      }
    }

    if (mode === 'brush' && onBrushStroke && !isDraggingRef.current) {
      isDrawingRef.current = true
      currentStrokeRef.current = {
        id: `brush-${Date.now()}-${Math.random()}`,
        points: [position],
        color: brushColor,
        size: brushSize,
        brushType: brushType,
        timestamp: Date.now()
      }
      lastPointRef.current = position
    } else if (mode === 'sticker' && !isDraggingRef.current && !hit) {
      // Only place new sticker if not dragging and not clicking on existing item
      onStickerPlacement(position.x, position.y)
    }
  }

  // Handle move for brush drawing or dragging
  const handlePointerMove = (e: React.MouseEvent | React.TouchEvent): void => {
    const position = getClickPosition(e)
    if (!position) return

    // Handle dragging
    if (isDraggingRef.current && dragItemIdRef.current && dragOffsetRef.current) {
      if (dragItemTypeRef.current === 'sticker' && onStickerUpdate) {
        // Update sticker position
        const newX = position.x - dragOffsetRef.current.x
        const newY = position.y - dragOffsetRef.current.y
        onStickerUpdate(dragItemIdRef.current, newX, newY)
        // Trigger re-render
        if (renderFnRef.current) {
          renderFnRef.current()
        }
      } else if (dragItemTypeRef.current === 'brush' && onBrushStrokeUpdate) {
        // Update brush stroke points
        const stroke = brushStrokes.find((s) => s.id === dragItemIdRef.current)
        if (stroke && dragStartPosRef.current) {
          const deltaX = position.x - dragStartPosRef.current.x
          const deltaY = position.y - dragStartPosRef.current.y
          const newPoints = stroke.points.map((p) => ({
            x: p.x + deltaX,
            y: p.y + deltaY
          }))
          onBrushStrokeUpdate(dragItemIdRef.current, newPoints)
          dragStartPosRef.current = position
          // Trigger re-render
          if (renderFnRef.current) {
            renderFnRef.current()
          }
        }
      }
      return
    }

    // Handle brush drawing
    if (mode !== 'brush' || !isDrawingRef.current || !currentStrokeRef.current || !onBrushStroke) {
      return
    }

    // Add point to current stroke
    currentStrokeRef.current.points.push(position)
    lastPointRef.current = position

    // Trigger re-render to show preview - this ensures the preview uses the exact same
    // rendering logic as the main render function, guaranteeing perfect alignment
    // Debounce to ~60fps for smooth performance
    if (livePreviewTimeoutRef.current) {
      clearTimeout(livePreviewTimeoutRef.current)
    }
    livePreviewTimeoutRef.current = setTimeout(() => {
      if (renderFnRef.current) {
        renderFnRef.current()
      }
    }, 16) // ~60fps debounce
  }

  // Handle pointer up for brush drawing or dragging
  const handlePointerUp = (): void => {
    // Clear any pending live preview renders
    if (livePreviewTimeoutRef.current) {
      clearTimeout(livePreviewTimeoutRef.current)
      livePreviewTimeoutRef.current = null
    }

    // Handle drag end
    if (isDraggingRef.current) {
      isDraggingRef.current = false
      dragStartPosRef.current = null
      dragOffsetRef.current = null
      dragItemIdRef.current = null
      dragItemTypeRef.current = null
      // Re-render to ensure everything is properly drawn
      if (renderFnRef.current) {
        renderFnRef.current()
      }
      return
    }

    // Handle brush drawing end
    if (mode === 'brush' && isDrawingRef.current && currentStrokeRef.current && onBrushStroke) {
      // Complete the stroke
      if (currentStrokeRef.current.points.length > 0) {
        onBrushStroke(currentStrokeRef.current)
      }
      isDrawingRef.current = false
      currentStrokeRef.current = null
      lastPointRef.current = null
      // Re-render to ensure everything is properly drawn
      if (renderFnRef.current) {
        renderFnRef.current()
      }
    }
  }

  // Render function - optimized to prevent flickering
  const render = useCallback(async () => {
    const canvas = canvasRef.current
    const ctx = canvas?.getContext('2d')
    if (!canvas || !ctx || !isImageLoaded || !stripImageRef.current) return

    // Use device pixel ratio for high DPI displays
    const dpr = window.devicePixelRatio || 1
    const displayWidth = canvasDimensions.width
    const displayHeight = canvasDimensions.height

    // Set canvas size with device pixel ratio for crisp rendering
    canvas.width = displayWidth * dpr
    canvas.height = displayHeight * dpr

    // Scale context to match device pixel ratio
    ctx.scale(dpr, dpr)

    // Enable high-quality rendering
    ctx.imageSmoothingEnabled = true
    ctx.imageSmoothingQuality = 'high'

    // Clear canvas
    ctx.clearRect(0, 0, displayWidth, displayHeight)

    // Use cached strip image
    const img = stripImageRef.current

    // Calculate aspect ratio to fit image in canvas (using display dimensions)
    const imageAspect = imageDimensions.width / imageDimensions.height
    const canvasAspect = displayWidth / displayHeight

    let drawWidth, drawHeight, drawX, drawY

    if (imageAspect > canvasAspect) {
      // Image is wider than canvas
      drawWidth = displayWidth
      drawHeight = displayWidth / imageAspect
      drawX = 0
      drawY = (displayHeight - drawHeight) / 2
    } else {
      // Image is taller than canvas
      drawHeight = displayHeight
      drawWidth = displayHeight * imageAspect
      drawX = (displayWidth - drawWidth) / 2
      drawY = 0
    }

    // Draw the strip image (no need to create new Image object)
    ctx.drawImage(img, drawX, drawY, drawWidth, drawHeight)

    // Draw stickers
    const scaleX = drawWidth / imageDimensions.width
    const scaleY = drawHeight / imageDimensions.height

    for (const placement of stickerPlacements) {
      try {
        const stickerKey = placement.stickerId
        let stickerImg = stickerImagesCache.current.get(stickerKey)

        if (!stickerImg) {
          // Find sticker by ID from stickers array
          const sticker = stickers.find((s) => s._id === stickerKey)
          if (!sticker) {
            console.warn(`Sticker ${stickerKey} not found in stickers array`)
            continue
          }

          // Try to get from loading promise if available
          const loadingPromise = stickerLoadingPromises.current.get(stickerKey)
          if (loadingPromise) {
            // Image is still loading, skip for now (will be drawn on next render)
            continue
          }

          // Create new image if not cached and not loading
          stickerImg = new Image()
          stickerImg.crossOrigin = 'anonymous'
          stickerImg.src = sticker.url || ''
          stickerImagesCache.current.set(stickerKey, stickerImg)
        }

        // Only draw if image is loaded
        if (stickerImg && stickerImg.complete && stickerImg.naturalWidth > 0) {
          // Calculate sticker dimensions maintaining aspect ratio
          const stickerAspect = stickerImg.naturalWidth / stickerImg.naturalHeight

          // Scale sticker position to canvas coordinates
          const stickerX = placement.x * scaleX + drawX - (placement.size * scaleX) / 2
          const stickerY = placement.y * scaleY + drawY - (placement.size * scaleY) / 2

          // Calculate sticker size maintaining aspect ratio
          // Use the placement.size as the base dimension, then calculate the other dimension
          let stickerWidth: number
          let stickerHeight: number

          if (stickerAspect >= 1) {
            // Sticker is wider or square - use size as width
            stickerWidth = placement.size * Math.max(scaleX, scaleY)
            stickerHeight = stickerWidth / stickerAspect
          } else {
            // Sticker is taller - use size as height
            stickerHeight = placement.size * Math.max(scaleX, scaleY)
            stickerWidth = stickerHeight * stickerAspect
          }

          // Ensure high-quality rendering
          ctx.imageSmoothingEnabled = true
          ctx.imageSmoothingQuality = 'high'
          ctx.drawImage(stickerImg, stickerX, stickerY, stickerWidth, stickerHeight)
        }
      } catch (error) {
        console.error('Failed to render sticker:', error)
      }
    }

    // Draw brush strokes (including the current stroke being drawn for live preview)
    const allStrokes = currentStrokeRef.current
      ? [...brushStrokes, currentStrokeRef.current]
      : brushStrokes

    for (const stroke of allStrokes) {
      if (stroke.points.length === 0) continue

      ctx.save()
      ctx.strokeStyle = stroke.color
      let lineWidth = stroke.size * Math.max(scaleX, scaleY)
      ctx.lineCap = 'round'
      ctx.lineJoin = 'round'

      // Apply brush type styling
      switch (stroke.brushType) {
        case 'dashed':
          ctx.setLineDash([Math.max(4, lineWidth * 0.3), Math.max(4, lineWidth * 0.3)])
          break
        case 'dotted':
          ctx.setLineDash([Math.max(1, lineWidth * 0.1), Math.max(3, lineWidth * 0.2)])
          ctx.lineCap = 'round'
          break
        case 'marker':
          ctx.globalAlpha = 0.6
          lineWidth = lineWidth * 1.8
          ctx.lineWidth = lineWidth
          // Add slight blur effect for marker
          ctx.shadowBlur = lineWidth * 0.3
          ctx.shadowColor = stroke.color
          break
        case 'pencil':
          ctx.globalAlpha = 0.85
          lineWidth = lineWidth * 0.7
          ctx.lineWidth = lineWidth
          // Add texture effect for pencil
          ctx.shadowBlur = lineWidth * 0.2
          ctx.shadowColor = stroke.color
          break
        case 'airbrush':
          ctx.globalAlpha = 0.4
          lineWidth = lineWidth * 1.5
          ctx.lineWidth = lineWidth
          // Add soft blur for airbrush
          ctx.shadowBlur = lineWidth * 0.5
          ctx.shadowColor = stroke.color
          break
        case 'solid':
        default:
          ctx.setLineDash([])
          ctx.lineWidth = lineWidth
          break
      }

      // Draw the stroke
      ctx.beginPath()
      const startPoint = stroke.points[0]
      const startX = startPoint.x * scaleX + drawX
      const startY = startPoint.y * scaleY + drawY
      ctx.moveTo(startX, startY)

      // For smoother curves, use quadratic curves between points
      if (stroke.points.length > 2) {
        for (let i = 1; i < stroke.points.length - 1; i++) {
          const currentPoint = stroke.points[i]
          const nextPoint = stroke.points[i + 1]
          const midX = (currentPoint.x + nextPoint.x) / 2
          const midY = (currentPoint.y + nextPoint.y) / 2
          const x = currentPoint.x * scaleX + drawX
          const y = currentPoint.y * scaleY + drawY
          const controlX = midX * scaleX + drawX
          const controlY = midY * scaleY + drawY
          ctx.quadraticCurveTo(x, y, controlX, controlY)
        }
        // Draw to last point
        const lastPoint = stroke.points[stroke.points.length - 1]
        ctx.lineTo(lastPoint.x * scaleX + drawX, lastPoint.y * scaleY + drawY)
      } else {
        // For short strokes, just draw a line
        for (let i = 1; i < stroke.points.length; i++) {
          const point = stroke.points[i]
          ctx.lineTo(point.x * scaleX + drawX, point.y * scaleY + drawY)
        }
      }

      ctx.stroke()

      // Reset shadow and alpha
      ctx.shadowBlur = 0
      ctx.globalAlpha = 1
      ctx.restore()
    }

    // Draw selection helpers (only on canvas, not in final export)
    if (selectedItemId) {
      const drawArea = getDrawArea()
      if (drawArea) {
        const { scaleX, scaleY } = drawArea

        // Find selected item
        const selectedSticker = stickerPlacements.find((p) => p.id === selectedItemId)
        const selectedStroke = brushStrokes.find((s) => s.id === selectedItemId)

        if (selectedSticker) {
          // Draw selection rectangle for sticker
          const sticker = stickers.find((s) => s._id === selectedSticker.stickerId)
          if (sticker) {
            let stickerImg = stickerImagesCache.current.get(selectedSticker.stickerId)
            if (!stickerImg) {
              stickerImg = new Image()
              stickerImg.crossOrigin = 'anonymous'
              stickerImg.src = sticker.url
              stickerImagesCache.current.set(selectedSticker.stickerId, stickerImg)
            }

            if (stickerImg.complete && stickerImg.naturalWidth > 0) {
              const stickerAspect = stickerImg.naturalWidth / stickerImg.naturalHeight
              let stickerWidth: number, stickerHeight: number

              if (stickerAspect >= 1) {
                stickerWidth = selectedSticker.size * Math.max(scaleX, scaleY)
                stickerHeight = stickerWidth / stickerAspect
              } else {
                stickerHeight = selectedSticker.size * Math.max(scaleX, scaleY)
                stickerWidth = stickerHeight * stickerAspect
              }

              // Use the same calculation as sticker rendering to ensure exact match
              const stickerX = selectedSticker.x * scaleX + drawX - (selectedSticker.size * scaleX) / 2
              const stickerY = selectedSticker.y * scaleY + drawY - (selectedSticker.size * scaleY) / 2

              ctx.save()
              // Context is already scaled with dpr at the start of render
              // Draw dashed selection rectangle (no vertices since there's no resize functionality)
              ctx.strokeStyle = '#3B82F6'
              ctx.lineWidth = 2
              ctx.setLineDash([5, 5])
              ctx.strokeRect(stickerX, stickerY, stickerWidth, stickerHeight)
              ctx.restore()
            }
          }
        } else if (selectedStroke && selectedStroke.points.length > 0) {
          // Draw selection helpers for brush stroke
          ctx.save()
          // Context is already scaled with dpr at the start of render
          // Draw bounding box around stroke
          let minX = Infinity
          let minY = Infinity
          let maxX = -Infinity
          let maxY = -Infinity

          selectedStroke.points.forEach((p) => {
            const x = p.x * scaleX + drawX
            const y = p.y * scaleY + drawY
            minX = Math.min(minX, x)
            minY = Math.min(minY, y)
            maxX = Math.max(maxX, x)
            maxY = Math.max(maxY, y)
          })

          // Add padding for stroke width
          const padding = (selectedStroke.size * Math.max(scaleX, scaleY)) / 2 + 5
          minX -= padding
          minY -= padding
          maxX += padding
          maxY += padding

          // Draw dashed selection rectangle (no vertices since there's no resize functionality)
          ctx.strokeStyle = '#3B82F6'
          ctx.lineWidth = 2
          ctx.setLineDash([5, 5])
          ctx.strokeRect(minX, minY, maxX - minX, maxY - minY)
          ctx.restore()
        }
      }
    }
  }, [stickerPlacements, isImageLoaded, canvasDimensions, imageDimensions, stickers, brushStrokes, selectedItemId, getDrawArea])

  // Store render function in ref so it can be called without causing dependency issues
  useEffect(() => {
    renderFnRef.current = render
  }, [render])

  // Render when dependencies change - debounced to prevent excessive renders
  useEffect(() => {
    if (renderTimeoutRef.current) {
      clearTimeout(renderTimeoutRef.current)
    }

    renderTimeoutRef.current = setTimeout(() => {
      render()
    }, 16) // ~60fps debounce

    return (): void => {
      if (renderTimeoutRef.current) {
        clearTimeout(renderTimeoutRef.current)
      }
    }
  }, [render])

  return (
    <div
      ref={containerRef}
      className={className}
      style={{
        width: '100%',
        height: '100%',
        position: 'relative',
        cursor: 'crosshair'
      }}
    >
      <canvas
        ref={canvasRef}
        onMouseDown={handlePointerDown}
        onMouseMove={handlePointerMove}
        onMouseUp={handlePointerUp}
        onMouseLeave={handlePointerUp}
        onTouchStart={handlePointerDown}
        onTouchMove={handlePointerMove}
        onTouchEnd={handlePointerUp}
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'contain',
          display: 'block',
          imageRendering: 'auto',
          cursor: 'crosshair',
          touchAction: mode === 'brush' ? 'none' : 'auto'
        }}
      />
    </div>
  )
}

export default StickerEditor
